import { ToggleGroup } from 'radix-ui';
import { cn } from '../../lib/cn';
import { useControlled } from '../../lib/use-controlled';
import { Icon, type LucideIcon } from './icon';

export interface ChipItem {
  value: string;
  label: string;
  count?: number;
  icon?: LucideIcon;
}

const chip = cn(
  'inline-flex h-[30px] touch:h-9 cursor-pointer items-center gap-1.5 rounded-full border border-border bg-muted px-3 text-[13px] font-medium leading-[18px] text-foreground transition-colors duration-[120ms] hover:bg-accent',
  'data-[state=on]:border-highlight data-[state=on]:bg-highlight-soft data-[state=on]:text-highlight',
);

function Chips({ items }: { items: ChipItem[] }) {
  return items.map((it) => (
    <ToggleGroup.Item key={it.value} value={it.value} className={cn(chip, 'group')}>
      {it.icon && <Icon icon={it.icon} size={15} />}
      {it.label}
      {it.count != null && <span className="font-mono text-[11px] text-muted-foreground group-data-[state=on]:text-inherit">{it.count}</span>}
    </ToggleGroup.Item>
  ));
}

interface Gemeinsam {
  items: ChipItem[];
  label?: string;
  className?: string;
}
export interface ChipGroupSingleProps extends Gemeinsam {
  multiple?: false;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
}
export interface ChipGroupMultipleProps extends Gemeinsam {
  multiple: true;
  value?: string[];
  defaultValue?: string[];
  onChange?: (value: string[]) => void;
}
export type ChipGroupProps = ChipGroupSingleProps | ChipGroupMultipleProps;

/** Filter und Schnellauswahl als Pills; die gewählte trägt highlight. */
export function ChipGroup(props: ChipGroupProps) {
  const cls = cn('flex flex-wrap gap-2', props.className);
  if (props.multiple) return <ChipGroupMultiple {...props} className={cls} />;
  return <ChipGroupSingle {...props} className={cls} />;
}

function ChipGroupSingle({ items, value, defaultValue, onChange, label, className }: ChipGroupSingleProps) {
  const [cur, set] = useControlled(value, defaultValue ?? items[0]?.value ?? '', onChange);
  return (
    // Einfachauswahl lässt sich nicht abwählen: leerer Wert wird ignoriert.
    <ToggleGroup.Root type="single" value={cur} onValueChange={(v) => v && set(v)} aria-label={label} className={className}>
      <Chips items={items} />
    </ToggleGroup.Root>
  );
}

function ChipGroupMultiple({ items, value, defaultValue, onChange, label, className }: ChipGroupMultipleProps) {
  const [cur, set] = useControlled(value, defaultValue ?? [], onChange);
  return (
    <ToggleGroup.Root type="multiple" value={cur} onValueChange={set} aria-label={label} className={className}>
      <Chips items={items} />
    </ToggleGroup.Root>
  );
}
